import { Controller } from "@hotwired/stimulus";

type Field = HTMLInputElement | HTMLSelectElement;
type Table = Record<string, Record<string, number> | null>;
type Preset = { school?: string; stats?: string; spellbook?: boolean; per_level: Table };
// slug, name, "Arcana 2º", the author's own, private
type SpellRow = [string, string, string, boolean, boolean];

const LEVELS = 15;
const STEP_RULES = ["chance_in_d6", "damage_multiplier"];
// What the sheet and the Oficina show after an ability's text: an ability holds one of these.
// A class sheet reads one ability of each of these.
const ONE_PER_CLASS = ["mastery", "rogue_talents"];
const SECTION_RULES = ["mastery", "rogue_talents", "select_one", "text_field", "spells", "rituals", "exclusive_spells"];
const RESULTS = 7;

type Row = { level: number; value: number; raw: string; levelInput: HTMLInputElement | null; valueInput: Field | null };

// The rules of one class ability card: which rules are in use, their fields, and
// the "Na ficha" line. The server renders every rule's block (hidden, fields
// disabled) and every sentence (phrases value); the Mago and Clérigo tables
// (and Bruxo's, for rituals) come from the database (tables value).
export default class extends Controller<HTMLElement> {
	static targets = ["name", "abilityLevel", "menu", "empty", "rule", "viewLevel", "viewLevelRow"];
	static values = { phrases: Object, tables: Object, spells: Array, own: Boolean };

	declare readonly nameTarget: HTMLInputElement;
	declare readonly abilityLevelTarget: HTMLSelectElement;
	declare readonly menuTarget: HTMLSelectElement;
	declare readonly emptyTarget: HTMLElement;
	declare readonly ruleTargets: HTMLElement[];
	declare readonly viewLevelTarget: HTMLSelectElement;
	declare readonly viewLevelRowTarget: HTMLElement;
	declare readonly phrasesValue: Record<string, string>;
	declare readonly tablesValue: Record<string, Preset>;
	declare readonly spellsValue: SpellRow[];
	declare readonly ownValue: boolean;

	connect() {
		// Sortable moves the card, which reconnects it: only the first connect may hydrate.
		if (!this.element.dataset.hydrated) {
			this.hydrate(JSON.parse(this.element.dataset.rules ?? "{}"));
			this.element.dataset.hydrated = "true";
		}
		this.update();
	}

	add() {
		const type = this.menuTarget.value;
		this.menuTarget.value = "";
		const rule = this.ruleFor(type);
		if (!rule) return;
		this.activate(rule, true);
		for (const list of this.lists(rule)) if (list.dataset.seed) this.pushRow(list);
		if (type === "select_one") for (let i = 0; i < 2; i++) this.pushOption(rule);
		this.update();
	}

	remove(event: Event) {
		const rule = (event.currentTarget as HTMLElement).closest<HTMLElement>("[data-rule]");
		if (!rule) return;
		this.activate(rule, false);
		this.reset(rule);
		this.update();
	}

	fill(event: Event) {
		const button = event.currentTarget as HTMLElement;
		const rule = button.closest<HTMLElement>("[data-rule]");
		if (!rule) return;
		for (const cell of this.cells(rule)) cell.value = button.dataset.fill === "one" ? "1" : (cell.dataset.level ?? "");
		this.update();
	}

	preset(event: Event) {
		const rule = (event.currentTarget as HTMLElement).closest<HTMLElement>("[data-rule]");
		const preset = this.tablesValue[(event.currentTarget as HTMLElement).dataset.preset ?? ""];
		if (!rule || !preset) return;
		this.fillSpells(rule, rule.dataset.rule === "rituals" ? { per_level: preset.per_level } : preset);
		this.update();
	}

	clear(event: Event) {
		const rule = (event.currentTarget as HTMLElement).closest<HTMLElement>("[data-rule]");
		if (!rule) return;
		for (const cell of this.cells(rule)) cell.value = "";
		this.update();
	}

	addOption(event: Event) {
		const rule = (event.currentTarget as HTMLElement).closest<HTMLElement>("[data-rule]");
		if (!rule) return;
		this.pushOption(rule);
		this.update();
	}

	removeOption(event: Event) {
		(event.currentTarget as HTMLElement).closest("[data-role=optionRow]")?.remove();
		this.update();
	}

	pickerSource(event: Event) {
		const button = event.currentTarget as HTMLElement;
		const picker = button.closest<HTMLElement>("[data-role=picker]");
		if (!picker) return;
		picker.dataset.source = button.dataset.source;
		this.renderPicker(picker);
	}

	pickerSearch(event: Event) {
		const picker = (event.currentTarget as HTMLElement).closest<HTMLElement>("[data-role=picker]");
		if (picker) this.renderPicker(picker);
	}

	pickerAdd(event: Event) {
		const button = event.currentTarget as HTMLElement;
		const picker = button.closest<HTMLElement>("[data-role=picker]");
		if (!picker || !button.dataset.slug) return;
		this.addChip(picker, button.dataset.slug);
		this.renderPicker(picker);
		this.update();
	}

	pickerRemove(event: Event) {
		const chip = (event.currentTarget as HTMLElement).closest<HTMLElement>("[data-role=chip]");
		const picker = chip?.closest<HTMLElement>("[data-role=picker]");
		if (!chip || !picker) return;
		chip.remove();
		this.renderPicker(picker);
		this.update();
	}

	addStep(event: Event) {
		const button = event.currentTarget as HTMLElement;
		const rule = button.closest<HTMLElement>("[data-rule]");
		const list = rule && this.list(rule, button.dataset.list ?? "");
		if (!list) return;
		this.pushRow(list);
		this.update();
	}

	removeStep(event: Event) {
		const row = (event.currentTarget as HTMLElement).closest<HTMLElement>("[data-role=stepRow]");
		const list = row?.closest<HTMLElement>("[data-list]");
		// A rule with a list emptied of its rows has no inputs and would be dropped on save: it keeps its minimum.
		if (!row || !list || this.steps(list).length <= Number(list.dataset.min ?? 0)) return;
		row.remove();
		this.update();
	}

	update() {
		const active = this.ruleTargets.filter((rule) => !rule.hidden);
		this.emptyTarget.hidden = active.length > 0;
		this.viewLevelRowTarget.hidden = active.length === 0;
		const used = new Set(active.map((rule) => rule.dataset.rule));
		const sectioned = active.some((rule) => SECTION_RULES.includes(rule.dataset.rule ?? ""));
		for (const option of this.menuTarget.options)
			option.hidden = option.disabled =
				used.has(option.value) ||
				((option.value === "spells" || ONE_PER_CLASS.includes(option.value)) && this.otherCardHas(option.value)) ||
				(sectioned && SECTION_RULES.includes(option.value));

		for (const rule of active) {
			this.syncPickers(rule);
			this.syncPrompt(rule);
			for (const list of this.lists(rule)) {
				const only = this.steps(list).length <= Number(list.dataset.min ?? 0);
				for (const button of list.querySelectorAll<HTMLButtonElement>('[data-action*="class-rules#removeStep"]')) button.disabled = only;
			}
			this.nameRowValues(rule);
			for (const cell of this.cells(rule)) cell.dataset.filled = String(cell.value !== "" && Number(cell.value) > 0);
			const [text, invalid] = this.describe(rule);
			const line = rule.querySelector<HTMLElement>("[data-role=sheetLine]");
			if (line) line.dataset.invalid = String(invalid);
			// The lines are live regions: rewriting identical text would be announced again.
			this.write(rule, "sheet", text);
			this.write(rule, "workshop", this.workshop(rule));
			if (rule.dataset.rule === "rogue_talents") this.talentsExtras(rule);
		}
	}

	private write(rule: HTMLElement, role: string, text: string) {
		const target = rule.querySelector<HTMLElement>(`[data-role=${role}]`);
		if (target && target.textContent !== text) target.textContent = text;
	}

	// A saved ability opens with its rules; the browser keeps them on a reconnect.
	private hydrate(rules: Record<string, unknown>) {
		const kept: Record<string, Record<string, string>> = JSON.parse(this.element.dataset.kept ?? "{}");
		for (const [type, value] of Object.entries(rules)) {
			const rule = this.ruleFor(type);
			if (!rule) continue;
			this.activate(rule, true);
			this.showKept(rule, kept[type] ?? {});
			if (type === "spells" || type === "rituals") this.fillSpells(rule, value as Preset, (value as { allowlist?: string[] }).allowlist);
			else if (type === "exclusive_spells") this.fillList(rule, value as string[]);
			else if (type === "select_one" || type === "text_field") this.fillPrompt(rule, value as { prompt?: string; options?: string[] });
			else if (type === "daily_uses") this.fillLadder(rule, value);
			else if (STEP_RULES.includes(type)) this.fillRows(rule, "steps", value);
			else if (type === "mastery") this.fillMastery(rule, value as Record<string, unknown>);
			else if (type === "rogue_talents") this.fillTalents(rule, value as Record<string, unknown>);
			else if (type !== "bonus_damage_archery_mod_forca" && type !== "mastery_extra_attack") this.setScalar(rule, value);
		}
	}

	private fillMastery(rule: HTMLElement, value: Record<string, unknown>) {
		this.fillRows(rule, "choices", value.on_level);
		this.fillRows(rule, "bonus", value.bonus_on_level);
	}

	private fillTalents(rule: HTMLElement, value: Record<string, unknown>) {
		const picked = Array.isArray(value.talents) ? value.talents : [];
		for (const box of rule.querySelectorAll<HTMLInputElement>("input[type=checkbox]")) box.checked = picked.includes(box.value);
		const max = this.fieldIn(rule, '[data-key="max"]');
		if (max && value.max !== undefined) max.value = String(value.max);
		const points = value.on_level && typeof value.on_level === "object" ? Object.entries(value.on_level as Record<string, { points?: unknown }>) : [];
		this.fillRows(rule, "points", Object.fromEntries(points.map(([level, entry]) => [level, entry?.points])));
	}

	private setScalar(rule: HTMLElement, value: unknown) {
		const field = this.fieldIn(rule, "input[name], select[name]");
		if (field) field.value = String(value);
	}

	// What the rule keeps without a field: the casting attribute of rituals, a prompt's key and icon.
	private showKept(rule: HTMLElement, kept: Record<string, string>) {
		if (kept.stats) {
			const label = this.element.querySelector<HTMLOptionElement>(`[data-rule="spells"] [data-key="stats"] option[value="${kept.stats}"]`)?.text ?? kept.stats;
			this.show(rule, "stats", this.say("kept_stats", { stat: label }));
		}
		if (kept.icon) this.show(rule, "icon", this.say("kept_icon", { icon: kept.icon }));
		const key = rule.querySelector<HTMLElement>("[data-role=promptKey]");
		if (key && kept.key) key.textContent = kept.key;
	}

	private show(rule: HTMLElement, name: string, text: string) {
		const target = rule.querySelector<HTMLElement>(`[data-kept=${name}]`);
		if (!target) return;
		target.textContent = text;
		target.hidden = false;
	}

	private fillPrompt(rule: HTMLElement, value: { prompt?: string; options?: string[] }) {
		const prompt = this.fieldIn(rule, '[data-key="prompt"]');
		if (prompt) prompt.value = value.prompt ?? "";
		for (const option of value.options ?? []) this.pushOption(rule, option);
	}

	private fillSpells(rule: HTMLElement, preset: Preset, allowlist?: string[]) {
		if (allowlist) this.fillList(rule, allowlist);
		const school = this.fieldIn(rule, '[data-key="school"]');
		const stats = this.fieldIn(rule, '[data-key="stats"]');
		const book = this.fieldIn(rule, '[data-key="spellbook"]') as HTMLInputElement | null;
		if (school && preset.school) school.value = preset.school;
		if (stats && preset.stats) stats.value = preset.stats;
		if (book) book.checked = Boolean(preset.spellbook);
		const table = preset.per_level && typeof preset.per_level === "object" ? preset.per_level : {};
		for (const cell of this.cells(rule)) {
			const amount = table[cell.dataset.level ?? ""]?.[cell.dataset.circle ?? ""];
			cell.value = amount ? String(amount) : "";
		}
	}

	private fillLadder(rule: HTMLElement, value: unknown) {
		const ladder = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
		for (const cell of this.cells(rule)) {
			const amount = ladder[cell.dataset.level ?? ""];
			cell.value = amount === undefined || amount === null ? "" : String(amount);
		}
	}

	private fillRows(rule: HTMLElement, name: string, value: unknown) {
		const list = this.list(rule, name);
		const entries = value && typeof value === "object" ? Object.entries(value as Record<string, unknown>) : [];
		if (list) for (const [level, amount] of entries.sort(([a], [b]) => Number(a) - Number(b))) this.pushRow(list, level, String(amount));
	}

	// A row is a level and a value; the value is what is submitted, named after the level.
	private pushRow(list: HTMLElement, level?: string, value?: string) {
		const template = list.parentElement?.querySelector<HTMLTemplateElement>(`template[data-for="${list.dataset.list}"]`);
		const row = template?.content.cloneNode(true) as DocumentFragment | undefined;
		const levelInput = row?.querySelector<HTMLInputElement>("[data-role=stepLevel]");
		const valueInput = row?.querySelector<Field>("[data-role=stepValue]");
		if (!row || !levelInput || !valueInput) return;
		const last = this.steps(list).at(-1);
		levelInput.value = level ?? String(last ? Math.min(LEVELS, last.level + 3) : 1);
		if (value !== undefined) valueInput.value = value;
		else if (valueInput instanceof HTMLSelectElement) valueInput.value = last?.raw ?? valueInput.value;
		else
			valueInput.value = String(
				last ? Math.min(Number(valueInput.max), last.value + Number(list.dataset.valueStep ?? 0)) : Number(list.dataset.valueFirst ?? 1),
			);
		list.appendChild(row);
	}

	private pickers(rule: HTMLElement) {
		return [...rule.querySelectorAll<HTMLElement>("[data-role=picker]")];
	}

	private spell(slug: string): SpellRow {
		return this.spellsValue.find((row) => row[0] === slug) ?? [slug, this.say("spell_unknown", { slug }), "", false, false];
	}

	private chosen(picker: HTMLElement) {
		return [...picker.querySelectorAll<HTMLInputElement>("[data-role=chosen] [data-role=chipValue]")].map((field) => field.value);
	}

	// The closed list of a spells or rituals rule: its slugs, or null when the rule has no list.
	private closedList(rule: HTMLElement) {
		const picker = this.pickers(rule)[0];
		if (!picker?.querySelector<HTMLInputElement>("[data-role=closedList]")?.checked) return null;
		return this.chosen(picker);
	}

	private addChip(picker: HTMLElement, slug: string) {
		if (this.chosen(picker).includes(slug)) return;
		const chip = picker.querySelector<HTMLTemplateElement>("template[data-for=chip]")?.content.cloneNode(true) as DocumentFragment | undefined;
		const [, name, , mine] = this.spell(slug);
		const field = chip?.querySelector<HTMLInputElement>("[data-role=chipValue]");
		if (!chip || !field) return;
		field.value = slug;
		const label = chip.querySelector<HTMLElement>("[data-role=chipName]");
		if (label) label.textContent = name;
		const own = chip.querySelector<HTMLElement>("[data-role=chipMine]");
		if (own) own.hidden = !mine;
		const remove = chip.querySelector<HTMLElement>("[data-role=chipRemove]");
		remove?.setAttribute("aria-label", this.say("take_out", { name }));
		picker.querySelector("[data-role=chosen]")?.appendChild(chip);
	}

	private fillList(rule: HTMLElement, slugs: string[]) {
		const picker = this.pickers(rule)[0];
		if (!picker) return;
		const toggle = picker.querySelector<HTMLInputElement>("[data-role=closedList]");
		if (toggle) toggle.checked = true;
		for (const slug of slugs) this.addChip(picker, slug);
		this.renderPicker(picker);
	}

	private renderPicker(picker: HTMLElement) {
		const chosen = this.chosen(picker);
		const source = picker.dataset.source ?? "official";
		const norm = (text: string) =>
			text
				.normalize("NFD")
				.replace(/[\u0300-\u036f]/g, "")
				.toLowerCase();
		const query = norm(picker.querySelector<HTMLInputElement>("[data-role=pickerSearch]")?.value.trim() ?? "");
		const own = this.spellsValue.filter((row) => row[3]);
		const title = picker.querySelector<HTMLElement>("[data-role=pickedTitle]");
		if (title) title.textContent = this.say("picked", { count: chosen.length });
		const none = picker.querySelector<HTMLElement>("[data-role=chosenNone]");
		if (none) none.hidden = chosen.length > 0;

		const ownTab = picker.querySelector<HTMLElement>("[data-role=ownTab]");
		if (ownTab) {
			ownTab.hidden = !this.ownValue;
			ownTab.textContent = this.say("own_spells", { count: own.length });
		}
		for (const tab of picker.querySelectorAll<HTMLElement>("[data-source]")) {
			const active = tab.dataset.source === source;
			tab.setAttribute("aria-pressed", String(active));
			tab.classList.toggle("odo-button__primary", active);
			tab.classList.toggle("odo-button__alt", !active);
		}
		const scope = picker.querySelector<HTMLElement>("[data-role=scope]");
		if (scope) scope.textContent = this.say(this.ownValue ? "spells_scope" : "spells_scope_admin");

		const found = this.spellsValue.filter((row) => row[3] === (source === "own") && (!query || norm(row[1]).includes(query)));
		const template = picker.querySelector<HTMLTemplateElement>("template[data-for=result]");
		const results = picker.querySelector<HTMLElement>("[data-role=results]");
		if (template && results)
			results.replaceChildren(
				...found.slice(0, RESULTS).map(([slug, name, label, , isPrivate]) => {
					const row = template.content.cloneNode(true) as DocumentFragment;
					const listed = chosen.includes(slug);
					const text = row.querySelector<HTMLElement>("[data-role=resultName]");
					if (text) text.textContent = name;
					const tag = row.querySelector<HTMLElement>("[data-role=resultLabel]");
					if (tag) tag.textContent = listed ? this.say("in_list") : `${label}${isPrivate ? ` · ${this.say("private_spell")}` : ""}`;
					const add = row.querySelector<HTMLButtonElement>("[data-role=resultAdd]");
					if (add) {
						add.dataset.slug = slug;
						add.disabled = listed;
						add.setAttribute("aria-label", this.say("add_spell", { name }));
					}
					return row;
				}),
			);
		const empty = picker.querySelector<HTMLElement>("[data-role=noResult]");
		if (empty) empty.hidden = found.length > 0;

		const copy = picker.querySelector<HTMLElement>("[data-role=copyAlong]");
		const mine = chosen.map((slug) => this.spell(slug)).filter((row) => row[3]);
		if (copy) {
			copy.hidden = mine.length === 0;
			copy.textContent = this.say("copy_along", { spells: this.join(mine.map((row) => row[1])) });
		}
	}

	// A picker with a closed-list box is on while it is ticked; its fields post only then.
	private syncPickers(rule: HTMLElement) {
		for (const picker of this.pickers(rule)) {
			const toggle = picker.querySelector<HTMLInputElement>("[data-role=closedList]");
			const on = toggle ? toggle.checked : true;
			const body = picker.querySelector<HTMLElement>("[data-role=pickerBody]");
			if (body) body.hidden = !on;
			for (const field of picker.querySelectorAll<HTMLInputElement>("input[name]")) field.disabled = !on;
		}
	}

	private pushOption(rule: HTMLElement, value = "") {
		const row = rule.querySelector<HTMLTemplateElement>("template[data-for=option]")?.content.cloneNode(true) as DocumentFragment | undefined;
		const field = row?.querySelector<HTMLInputElement>("[data-role=optionValue]");
		if (!row || !field) return;
		field.value = value;
		rule.querySelector("[data-role=optionList]")?.appendChild(row);
	}

	private options(rule: HTMLElement) {
		return [...rule.querySelectorAll<HTMLInputElement>("[data-role=optionValue]")];
	}

	// The prompt's question and options, the preview of the Oficina's field and the repeated-option check.
	private syncPrompt(rule: HTMLElement) {
		const type = rule.dataset.rule;
		if (type !== "select_one" && type !== "text_field") return;
		const prompt = this.fieldIn(rule, '[data-key="prompt"]')?.value.trim() ?? "";
		const name = this.nameTarget.value.trim() || this.say("name_blank");
		this.write(rule, "previewName", name);
		this.write(rule, "builderAsks", this.say("builder_asks", { level: this.abilityLevelTarget.value }));
		const fields = this.options(rule);
		const values = fields.map((field) => field.value.trim());
		fields.forEach((field, index) => {
			field.setAttribute("aria-label", this.say("option_aria", { n: index + 1 }));
			field
				.closest("[data-role=optionRow]")
				?.querySelector("[data-role=optionRemove]")
				?.setAttribute("aria-label", this.say("option_remove", { n: index + 1 }));
			field.setCustomValidity(values[index] !== "" && values.indexOf(values[index]) !== index ? this.say("options_bad") : "");
		});
		const select = rule.querySelector<HTMLSelectElement>("[data-role=previewSelect]");
		if (select) {
			const picked = select.value;
			const choices = values.filter((value, index) => value !== "" && values.indexOf(value) === index);
			// The card refreshes on focus: rebuilding an open list would close it.
			const signature = [prompt, ...choices].join("\u0001");
			if (select.dataset.signature !== signature) {
				select.dataset.signature = signature;
				select.replaceChildren(new Option(prompt || this.say("prompt_default"), ""), ...choices.map((value) => new Option(value, value)));
				select.value = choices.includes(picked) ? picked : "";
			}
		}
		const text = rule.querySelector<HTMLInputElement>("[data-role=previewText]");
		if (text) text.placeholder = prompt;
	}

	private lists(rule: HTMLElement) {
		return [...rule.querySelectorAll<HTMLElement>("[data-list]:not(button)")];
	}

	private list(rule: HTMLElement, name: string) {
		return this.lists(rule).find((list) => list.dataset.list === name);
	}

	private steps(list: HTMLElement | undefined): Row[] {
		return [...(list?.querySelectorAll<HTMLElement>("[data-role=stepRow]") ?? [])].map((row) => {
			const levelInput = row.querySelector<HTMLInputElement>("[data-role=stepLevel]");
			const valueInput = row.querySelector<Field>("[data-role=stepValue]");
			return { level: Number(levelInput?.value), value: Number(valueInput?.value), raw: valueInput?.value ?? "", levelInput, valueInput };
		});
	}

	// What a list's rows get wrong: a level out of range, a value under 1, or two rows on one level.
	private problem(list: HTMLElement | undefined, numeric: boolean) {
		const rows = this.steps(list);
		if (rows.some((row) => !(row.level >= 1 && row.level <= LEVELS) || (numeric && !(row.value >= 1)))) return "invalid";
		return new Set(rows.map((row) => row.level)).size === rows.length ? null : "rows_repeat";
	}

	// The row's value is named by its level and the list's path ("[on_level][%l]"), so the form posts keyed by level.
	private nameRowValues(rule: HTMLElement) {
		const base = this.element.querySelector<HTMLInputElement>('input[name$="[rules][sent]"]')?.name.replace("[sent]", "") ?? "";
		for (const list of this.lists(rule)) {
			const seen = new Set<number>();
			for (const { level, levelInput, valueInput } of this.steps(list)) {
				if (!valueInput) continue;
				const path = (list.dataset.path ?? "").replace("%l", String(level));
				valueInput.name = Number.isInteger(level) && level >= 1 && level <= LEVELS ? `${base}[${rule.dataset.rule}]${path}` : "";
				levelInput?.setCustomValidity(seen.has(level) ? this.say("rows_repeat") : "");
				seen.add(level);
			}
		}
	}

	private activate(rule: HTMLElement, on: boolean) {
		rule.hidden = !on;
		for (const field of this.fieldsIn(rule)) if (field.name) field.disabled = !on;
	}

	private reset(rule: HTMLElement) {
		for (const list of this.lists(rule)) list.replaceChildren();
		for (const box of rule.querySelectorAll("[data-role=chosen], [data-role=optionList]")) box.replaceChildren();
		for (const picker of this.pickers(rule)) this.renderPicker(picker);
		for (const field of this.fieldsIn(rule)) {
			if (field instanceof HTMLSelectElement) for (const option of field.options) option.selected = option.defaultSelected;
			else if (field.type === "checkbox") field.checked = field.defaultChecked;
			else field.value = field.defaultValue;
		}
	}

	// The rules of this type in use in the form's cards, this one's and the others' (a card removed from the form is hidden).
	private liveRules(type: string) {
		return [...document.querySelectorAll<HTMLElement>(`[data-controller~="class-rules"] [data-rule="${type}"]:not([hidden])`)].filter(
			(rule) => rule.closest<HTMLElement>("[data-nested-items-target=item]")?.style.display !== "none",
		);
	}

	// A class sheet reads the spell slots, the mastery and the rogue talents of one ability only.
	private otherCardHas(type: string) {
		return this.liveRules(type).some((rule) => rule.closest<HTMLElement>('[data-controller~="class-rules"]') !== this.element);
	}

	// The level of the ability a rule sits in.
	private abilityLevelOf(rule: HTMLElement) {
		return Number(
			rule.closest<HTMLElement>('[data-controller~="class-rules"]')?.querySelector<HTMLSelectElement>('[data-class-rules-target="abilityLevel"]')?.value,
		);
	}

	private ruleFor(type: string) {
		return this.ruleTargets.find((rule) => rule.dataset.rule === type);
	}

	private fieldsIn(root: HTMLElement) {
		return [...root.querySelectorAll<Field>("input, select")];
	}

	private fieldIn(root: HTMLElement, selector: string) {
		return root.querySelector<Field>(selector);
	}

	private cells(rule: HTMLElement) {
		return [...rule.querySelectorAll<HTMLInputElement>("input[data-level]")];
	}

	private say(key: string, vars: Record<string, string | number> = {}) {
		return (this.phrasesValue[key] ?? key).replace(/%\{(\w+)\}/g, (_, name) => String(vars[name] ?? ""));
	}

	private join(parts: string[]) {
		if (parts.length < 2) return parts.join("");
		return `${parts.slice(0, -1).join(", ")} ${this.say("and")} ${parts.at(-1)}`;
	}

	private describe(rule: HTMLElement): [string, boolean] {
		const type = rule.dataset.rule ?? "";
		const level = Number(this.viewLevelTarget.value);
		const name = this.nameTarget.value.trim() || this.say("name_blank");
		const invalid = (key = "invalid"): [string, boolean] => [this.say(key), true];
		const number = (selector: string) => Number(this.fieldIn(rule, selector)?.value);

		// The sheet applies an ability from its own level, except the spell slots and the hit die, which carry their own wording.
		const from = Number(this.abilityLevelTarget.value);
		if (type !== "spells" && type !== "replace_hp_die" && level < from) return [this.say("not_yet", { name, level }), false];

		switch (type) {
			case "spells": {
				const cells = this.cells(rule).filter((cell) => Number(cell.value) > 0);
				if (cells.length === 0) return invalid("spells_invalid");
				const row = cells.filter((cell) => cell.dataset.level === String(level)).sort((a, b) => Number(a.dataset.circle) - Number(b.dataset.circle));
				if (row.length === 0) return [this.say("spells_none", { level }), false];
				const list = row.map((cell, index) => this.say(index === 0 ? "circle_first" : "circle_next", { n: cell.value, circle: cell.dataset.circle ?? "" }));
				const school = this.fieldIn(rule, '[data-key="school"]') as HTMLSelectElement;
				const stats = this.fieldIn(rule, '[data-key="stats"]') as HTMLSelectElement;
				const book = this.fieldIn(rule, '[data-key="spellbook"]') as HTMLInputElement;
				const closed = this.closedList(rule);
				const line = this.say("spells", {
					list: this.join(list),
					school: this.say(`school_${school.value}`),
					stat: stats.selectedOptions[0]?.text ?? "",
					book: book.checked ? this.say("book") : "",
				});
				if (closed === null) return [line, false];
				return closed.length === 0
					? [`${line} ${this.say("list_empty")}`, true]
					: [line + this.say(closed.length === 1 ? "spells_closed_one" : "spells_closed_other", { n: closed.length }), false];
			}
			case "rituals": {
				const cells = this.cells(rule).filter((cell) => Number(cell.value) > 0);
				if (cells.length === 0) return invalid("spells_invalid");
				const row = cells.filter((cell) => cell.dataset.level === String(level)).sort((a, b) => Number(a.dataset.circle) - Number(b.dataset.circle));
				if (row.length === 0) return [this.say("rituals_none", { level }), false];
				const uses = row
					.map((cell) =>
						this.say(Number(cell.value) === 1 ? "rituals_use_one" : "rituals_use_other", { name, circle: cell.dataset.circle ?? "", n: cell.value }),
					)
					.join("; ");
				const closed = this.closedList(rule);
				if (closed === null)
					return [`${uses}. ${this.say("rituals_open", { school: this.say(`school_${this.fieldIn(rule, '[data-key="school"]')?.value}`) })}`, false];
				if (closed.length === 0) return [`${uses}. ${this.say("list_empty")}`, true];
				return [`${uses}. ${this.say(closed.length === 1 ? "rituals_closed_one" : "rituals_closed_other", { n: closed.length })}`, false];
			}
			case "exclusive_spells": {
				const names = this.chosen(this.pickers(rule)[0]).map((slug) => this.spell(slug)[1]);
				if (names.length === 0) return invalid("exclusive_empty");
				return [this.say(names.length === 1 ? "exclusive_one" : "exclusive_other", { list: this.join(names) }), false];
			}
			case "select_one": {
				if (!this.fieldIn(rule, '[data-key="prompt"]')?.value.trim()) return invalid("prompt_invalid");
				const values = this.options(rule).map((field) => field.value.trim());
				const filled = values.filter((value) => value !== "");
				if (filled.length < 2) return invalid("options_few");
				if (filled.length !== values.length || new Set(values).size !== values.length) return invalid("options_bad");
				return [this.choiceLine(rule, from), false];
			}
			case "text_field":
				if (!this.fieldIn(rule, '[data-key="prompt"]')?.value.trim()) return invalid("prompt_invalid");
				return [this.choiceLine(rule, from), false];
			case "daily_uses": {
				const cells = this.cells(rule);
				if (cells.every((cell) => cell.value === "")) return invalid();
				const uses = Number(cells.find((cell) => cell.dataset.level === String(level))?.value) || 0;
				if (uses <= 0) return [this.say("daily_uses_none", { name, level }), false];
				return [this.say(uses === 1 ? "daily_uses_one" : "daily_uses_other", { name, value: uses, level }), false];
			}
			case "chance_in_d6":
			case "damage_multiplier": {
				const list = this.list(rule, "steps");
				const steps = this.steps(list);
				if (steps.length === 0) return invalid("steps_empty");
				const problem = this.problem(list, true);
				if (problem) return invalid(problem);
				const current = this.latest(steps, level);
				if (!current) return [this.say(`${type}_none`, { name, level }), false];
				return [this.say(type, { name, value: current.value, level }), false];
			}
			case "bonus_hp": {
				const value = number("input[name]");
				if (!(value >= 1)) return invalid();
				return [this.say(type, { value, total: value * level, level }), false];
			}
			case "bonus_jpc": {
				const value = number("input[name]");
				return value >= 1 ? [this.say(type, { value }), false] : invalid();
			}
			case "replace_hp_die":
				// Level 1 keeps the class's fixed HP, and the sheet stops swapping the die after level 10.
				if (from > 10) return [this.say("replace_hp_die_none", { name }), false];
				return [this.say(type, { value: number("select[name]"), from: Math.max(2, from) }), false];
			case "mastery": {
				const choices = this.steps(this.list(rule, "choices"));
				const problem =
					choices.length === 0 ? "steps_empty" : (this.problem(this.list(rule, "choices"), false) ?? this.problem(this.list(rule, "bonus"), true));
				if (problem) return invalid(problem);
				const until = choices.filter((choice) => choice.level <= level);
				if (until.length === 0) return [this.say("mastery_none", { level }), false];
				const weapons = until.filter((choice) => choice.raw === "weapon").length;
				const list = this.join(
					[
						weapons > 0 ? this.say(weapons === 1 ? "mastery_weapons_one" : "mastery_weapons_other", { n: weapons }) : "",
						until.some((choice) => choice.raw === "damage_types") ? this.say("mastery_groups") : "",
					].filter(Boolean),
				);
				const bonus = this.latest(this.steps(this.list(rule, "bonus")), level);
				return [this.say(bonus ? "mastery" : "mastery_no_bonus", { level, list, bonus: bonus?.value ?? 0 }), false];
			}
			case "mastery_extra_attack": {
				const masteries = this.liveRules("mastery");
				if (masteries.length === 0) return invalid("mastery_extra_attack_alone");
				const picked = masteries.some(
					(mastery) => this.abilityLevelOf(mastery) <= level && this.steps(this.list(mastery, "choices")).some((choice) => choice.level <= level),
				);
				return [this.say(picked ? type : "mastery_extra_attack_none"), false];
			}
			case "rogue_talents": {
				const list = this.list(rule, "points");
				const rows = this.steps(list);
				const problem = rows.length === 0 ? "steps_empty" : this.problem(list, true);
				if (problem) return invalid(problem);
				const max = number('[data-key="max"]');
				if (!(max >= 2 && max <= 6)) return invalid();
				const until = rows.filter((row) => row.level <= level).sort((a, b) => a.level - b.level);
				const total = until.reduce((sum, row) => sum + row.value, 0);
				if (total === 0) return [this.say("rogue_talents_none", { level }), false];
				const parts = until.map((row) => this.say("points_part", { n: row.value, level: row.level }));
				return [this.say(type, { level, total: this.points(total), parts: parts.join(", ") }), false];
			}
			default:
				return [this.say(type), false];
		}
	}

	// The sheet's "Escolhas:" line, with the answer typed or picked in the preview.
	private choiceLine(rule: HTMLElement, level: number) {
		const choice = (rule.querySelector<HTMLSelectElement | HTMLInputElement>("[data-role=previewSelect], [data-role=previewText]")?.value ?? "").trim();
		return this.say(choice ? "choice_line" : "choice_none", { level, choice });
	}

	private latest(rows: Row[], level: number) {
		return rows.filter((row) => row.level <= level).sort((a, b) => b.level - a.level)[0];
	}

	private points(n: number) {
		return this.say(n === 1 ? "points_one" : "points_other", { n });
	}

	// What the Oficina asks for at the example level, for the rules that add a step there.
	private workshop(rule: HTMLElement) {
		const type = rule.dataset.rule ?? "";
		const level = Number(this.viewLevelTarget.value);
		const from = Number(this.abilityLevelTarget.value);
		if (level < from) return this.say("not_yet", { name: this.nameTarget.value.trim() || this.say("name_blank"), level });
		if (type === "mastery_extra_attack") return this.say("workshop_extra_attack");
		const rows = this.steps(this.list(rule, type === "mastery" ? "choices" : "points"))
			.filter((row) => row.level <= level)
			.sort((a, b) => a.level - b.level);
		if (type === "mastery") {
			if (rows.length === 0) return this.say("workshop_mastery_none", { level });
			const what = (row: Row) => this.say(row.raw === "weapon" ? "workshop_mastery_weapon" : "workshop_mastery_groups");
			return `${rows.map((row) => this.say("workshop_mastery_level", { level: row.level, what: what(row) })).join(" · ")}.`;
		}
		if (type !== "rogue_talents") return "";
		if (rows.length === 0) return this.say("workshop_talents_none", { level });
		const parts = rows.map((row) => this.say(row.value === 1 ? "workshop_talents_one" : "workshop_talents_other", { level: row.level, n: row.value }));
		return this.say("workshop_talents", { parts: parts.join(" · ") });
	}

	// Under the talents rule: how many are ticked, the warnings, and a sample of the sheet's tiles.
	private talentsExtras(rule: HTMLElement) {
		const level = Number(this.viewLevelTarget.value);
		const max = Number(this.fieldIn(rule, '[data-key="max"]')?.value);
		const boxes = [...rule.querySelectorAll<HTMLInputElement>("input[type=checkbox]")].filter((box) => box.checked);
		const rows = this.steps(this.list(rule, "points")).sort((a, b) => a.level - b.level);
		this.write(
			rule,
			"count",
			this.say(boxes.length === 1 ? "talents_count_one" : "talents_count_other", { count: boxes.length }) +
				(boxes.length === 5 ? ` ${this.say("talents_five")}` : ""),
		);

		const warnings = boxes.length === 0 ? [this.say("talents_none")] : [];
		for (const row of rows)
			if (row.value > boxes.length)
				warnings.push(this.say(boxes.length === 1 ? "points_over_one" : "points_over_other", { level: row.level, points: row.value, count: boxes.length }));
		const warning = rule.querySelector<HTMLElement>("[data-role=warning]");
		if (warning) {
			warning.hidden = warnings.length === 0;
			this.write(rule, "warning", warnings.join(" "));
		}

		// Each level puts its points on the first talents of the list that are still under the maximum.
		const scores = new Map(boxes.map((box) => [box, 2]));
		for (const row of rows.filter((entry) => entry.level <= level))
			for (const box of boxes.filter((entry) => (scores.get(entry) ?? 0) < max).slice(0, row.value)) scores.set(box, (scores.get(box) ?? 0) + 1);
		const tiles = rule.querySelector<HTMLElement>("[data-role=tiles]");
		const template = rule.querySelector<HTMLTemplateElement>("template[data-for=tile]");
		if (tiles && template) {
			tiles.replaceChildren(
				...boxes.map((box) => {
					const tile = template.content.cloneNode(true) as DocumentFragment;
					const title = tile.querySelector<HTMLElement>("[data-role=tileTitle]");
					const chance = tile.querySelector<HTMLElement>("[data-role=tileChance]");
					if (title) title.textContent = box.dataset.title ?? box.value;
					if (chance) chance.textContent = this.say("tile_chance", { n: scores.get(box) ?? 2 });
					return tile;
				}),
			);
		}
		this.write(rule, "example", this.say("talents_example", { max }));
	}
}
